/**
 * A helper class intended to store a user input,
 * alongside any validation errors for that specific input.
 */
export class InputValueAndError<Type> {
    /** The actual value in the Input.
     * null for no value.
     */
    readonly value: Type | null;
    /** Marks if an error */
    readonly hasError: boolean;
    /** An optional custom message overriding */
    readonly errorMessage?: string;
    /** Validate the value. Takes the current value and returns if there should be an error or not and an optional
     * custom error message. */
    readonly validationFunction: (thiss: InputValueAndError<Type>) => [boolean, string?];

    constructor(
        value: Type | null,
        validationFunction: (thiss: InputValueAndError<Type>) => [boolean, string?],
        hasError?: boolean,
        errorMsg?: string,
    ) {
        this.value = value;
        this.validationFunction = validationFunction;
        this.errorMessage = errorMsg;
        this.hasError = hasError ?? false;
    }

    /**
     * Checks if the value exists.
     * (Also type magic so its no longer optional)
     */
    hasValue(): this is { value: Type } {
        // Do a truthy check with strings
        if (typeof this.value === 'string') {
            return !!this.value;
        }

        return this.value !== null;
    }

    hasNoValue() {
        return !this.hasValue();
    }

    /**
     * Creates a new InputValueAndError from the existing, setting an error.
     * Clears any existing custom error message if none provided.
     */
    withError(errorMessage?: string): InputValueAndError<Type> {
        return new InputValueAndError(this.value, this.validationFunction, true, errorMessage);
    }

    withValue(newValue: Type) {
        return new InputValueAndError(newValue, this.validationFunction, this.hasError, this.errorMessage);
    }

    /**
     * Returns a new copy where the hasError has been set to the result of the validationFunction.
     */
    validated() {
        const [isValid, message] = this.validationFunction(this);
        return new InputValueAndError(this.value, this.validationFunction, !isValid, message ?? this.errorMessage);
    }
}
